import { FC } from 'react';
import { PortfolioSummary as PortfolioSummaryType } from '@/types/index';

interface Props {
  summary: PortfolioSummaryType;
}

const fmt = (v: number | null | undefined, decimals = 0): string => {
  if (v == null) return '—';
  return new Intl.NumberFormat('fr-CH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(v);
};

const fmtChf = (v: number | null | undefined): string => {
  if (v == null) return '—';
  return `CHF ${fmt(v)}`;
};

const fmtPct = (v: number | null | undefined, decimals = 1): string => {
  if (v == null) return '—';
  return `${fmt(v, decimals)} %`;
};

const KpiCard: FC<{ label: string; value: string; sub?: string; color?: string }> = ({
  label, value, sub, color = 'text-gray-900 dark:text-gray-100',
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-5 py-4 flex flex-col gap-1">
    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
    <span className={`text-xl font-bold ${color}`}>{value}</span>
    {sub && <span className="text-xs text-gray-400 dark:text-gray-500">{sub}</span>}
  </div>
);

export const PortfolioSummary: FC<Props> = ({ summary }) => {
  const cashFlowColor = summary.totalCashFlow >= 0
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-red-600 dark:text-red-400';

  const ltvColor = summary.globalLTV == null ? 'text-gray-900 dark:text-gray-100'
    : summary.globalLTV > 0.8 ? 'text-red-600 dark:text-red-400'
    : summary.globalLTV > 0.65 ? 'text-amber-600 dark:text-amber-400'
    : 'text-emerald-600 dark:text-emerald-400';

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      <KpiCard
        label="Actifs"
        value={String(summary.propertyCount)}
        sub="dans le portefeuille"
      />
      <KpiCard
        label="Valeur totale"
        value={fmtChf(summary.totalMarketValue)}
        sub="valeur de marché"
      />
      <KpiCard
        label="Dette totale"
        value={fmtChf(summary.totalDebt)}
        sub={`Fonds propres ${fmtChf(summary.totalEquity)}`}
      />
      <KpiCard
        label="NOI annuel"
        value={fmtChf(summary.totalNOI)}
        sub="revenu net d'exploitation"
      />
      <KpiCard
        label="Cash-flow annuel"
        value={fmtChf(summary.totalCashFlow)}
        sub={`${fmtChf(Math.round(summary.totalCashFlow / 12))} / mois`}
        color={cashFlowColor}
      />
      <KpiCard
        label="LTV global"
        value={fmtPct(summary.globalLTV != null ? summary.globalLTV * 100 : null)}
        sub={summary.avgCapRate != null ? `Cap rate moy. ${fmtPct(summary.avgCapRate)}` : undefined}
        color={ltvColor}
      />
    </div>
  );
};
